import React from 'react';
import { Link } from 'react-router-dom';
import {
  SparklesIcon,
  ShieldCheckIcon,
  GlobeAltIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { BRANDING, ONECARE_LOGO } from '../../config/branding';

interface LandingFooterProps {
  showQuickLinks?: boolean;
  showCompanyInfo?: boolean;
  isCompanyPage?: boolean;
}

type FooterLink = {
  name: string;
  href: string;
  external?: boolean;
};

const footerLinks: {
  product: FooterLink[];
  resources: FooterLink[];
} = {
  product: [
    { name: 'Intra-Communications', href: '/onecare/platform/messaging' },
    { name: 'Smart Flow AI', href: '/onecare/platform/smart-flow' },
    { name: 'Patient Feedback', href: '/onecare/platform/patient-feedback' },
    { name: 'Platform Overview', href: '#solutions' }
  ],
  resources: [
    { name: 'Implementation Guide', href: '/docs/phase_6/INDEX.md', external: true },
    { name: 'API Reference', href: '/docs/api/API_REFERENCE.md', external: true },
    { name: 'Deployment Workflow', href: '/docs/deployment/DEPLOYMENT_WORKFLOW.md', external: true }
  ],
};


export const LandingFooter: React.FC<LandingFooterProps> = ({
  showQuickLinks = true,
  showCompanyInfo = true,
  isCompanyPage = false
}) => {
  const handleLinkClick = (linkName: string, href: string) => {
    // Track analytics
    if (window.gtag) {
      window.gtag('event', 'footer_link_click', {
        event_category: 'navigation',
        event_label: linkName,
        value: href.startsWith('#') ? 1 : 2
      });
    }

    // Handle smooth scroll for hash links
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const footerLogo = isCompanyPage ? '/aimepic-logo.png' : ONECARE_LOGO;
  const footerBrandTitle = isCompanyPage ? BRANDING.company.fullName : BRANDING.product.fullName;
  const footerDescription = isCompanyPage
    ? 'Aimepic builds secure digital software that solves real-life operational challenges.'
    : BRANDING.product.description;

  return (
    <footer className="landing-footer" role="contentinfo">
      <div className="footer-container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="footer-main-content grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 py-12 border-b border-gray-700">
          {/* Company Information */}
          {showCompanyInfo && (
            <div className="footer-company-section">
              {/* Logo and Title */}
              <div className="footer-logo">
                <img
                  src={footerLogo}
                  alt={(isCompanyPage ? BRANDING.company.name : BRANDING.product.name) + " Logo"}
                  className="footer-logo-image object-contain"
                  style={{ height: '80px', width: '80px' }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                {/* Fallback Icon */}
                <div className="footer-logo-fallback logo hidden">
                  <SparklesIcon className="h-6 w-6" aria-hidden="true" />
                </div>
                <h3 className="footer-title ml-3">{footerBrandTitle}</h3>
              </div>

              <p className="footer-description">
                {footerDescription}
              </p>

              {/* Key Features */}
              <div className="footer-features space-y-3 mb-6">
                <div className="footer-feature-item flex items-center text-sm text-gray-300">
                  <ShieldCheckIcon className="footer-feature-icon w-4 h-4 mr-3 text-green-400" aria-hidden="true" />
                  <span className="footer-feature-text">
                    {isCompanyPage ? 'Security-First Engineering' : 'HIPAA-Ready Architecture'}
                  </span>
                </div>
                <div className="footer-feature-item flex items-center text-sm text-gray-300">
                  <GlobeAltIcon className="footer-feature-icon w-4 h-4 mr-3 text-blue-400" aria-hidden="true" />
                  <span className="footer-feature-text">
                    {isCompanyPage ? 'Privacy by Design' : 'Multi-Hospital Isolation & Analytics'}
                  </span>
                </div>
                <div className="footer-feature-item flex items-center text-sm text-gray-300">
                  <SparklesIcon className="footer-feature-icon w-4 h-4 mr-3 text-purple-400" aria-hidden="true" />
                  <span className="footer-feature-text">
                    {isCompanyPage ? 'AI-Assisted Workflows' : 'Mobile Workforce & Push Infrastructure'}
                  </span>
                </div>
              </div>

              {/* Engagement + Aimepic branding inline */}
              <div className="footer-emergency-contact bg-white/5 rounded-lg p-4 border border-white/10">
                <div className="flex items-start gap-4 justify-between">
                  <div className="flex-1">
                    <div className="footer-emergency-header flex items-center mb-2 text-primary-200">
                      <ArrowTopRightOnSquareIcon className="footer-emergency-icon w-5 h-5 mr-2" aria-hidden="true" />
                      <h4 className="footer-emergency-title font-semibold">Let&apos;s build your roadmap</h4>
                    </div>
                    <div className="footer-emergency-details space-y-1 text-sm text-gray-200">
                      <p>Email <a href="mailto:contact-us@aimepic.com" className="text-primary-300 hover:text-primary-200">contact-us@aimepic.com</a> to schedule a discovery session.</p>
                      <p>Existing clients can reach our platform team at <a href="mailto:admin@aimepic.com" className="text-primary-300 hover:text-primary-200">admin@aimepic.com</a>.</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="text-xs text-gray-400 mb-1">Powered by</div>
                    <img
                      src="/aimepic-logo.png"
                      alt={`${BRANDING.company.name} Logo`}
                      className="h-10 w-auto object-contain mb-1"
                    />
                    <p className="text-xs text-gray-300 text-center">{BRANDING.company.name}</p>
                    <p className="text-[10px] text-gray-400 text-center">Intuitive Realities</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Links */}
          {showQuickLinks && (
            <>
              <div className="footer-services-section">
                <h3 className="footer-services-title text-lg font-semibold text-white mb-4">Product</h3>
                <ul className="footer-services-list space-y-3">
                  {footerLinks.product.map((link) => (
                    <li key={link.name} className="footer-service-item">
                      {link.external ? (
                        <a
                          href={link.href}
                          className="footer-service-link text-gray-300 hover:text-white transition-colors text-sm"
                          onClick={() => handleLinkClick(link.name, link.href)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {link.name}
                        </a>
                      ) : (
                        <Link
                          to={link.href}
                          className="footer-service-link text-gray-300 hover:text-white transition-colors text-sm"
                          onClick={() => handleLinkClick(link.name, link.href)}
                        >
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="footer-support-section">
                <h3 className="footer-support-title text-lg font-semibold text-white mb-4">Resources</h3>
                <ul className="footer-support-list space-y-3">
                  {footerLinks.resources.map((link) => (
                    <li key={link.name} className="footer-support-item">
                      <a
                        href={link.href}
                        className="footer-support-link text-gray-300 hover:text-white transition-colors text-sm"
                        onClick={() => handleLinkClick(link.name, link.href)}
                        target={link.external ? '_blank' : undefined}
                        rel={link.external ? 'noopener noreferrer' : undefined}
                      >
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

            </>
          )}
        </div>

        {/* Bottom Footer */}
        <div className="footer-bottom py-8">
          {/* Copyright and Legal */}
          <div className="footer-bottom-content text-center space-y-4">
            {/* Legal Links */}
            <div className="footer-legal-links flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-700 mb-4">
              <Link
                to="/privacy"
                className="footer-legal-link hover:text-gray-300 transition-colors"
                onClick={() => handleLinkClick('Privacy Policy', '/privacy')}
              >
                Privacy Policy
              </Link>
              <span className="footer-legal-separator text-gray-900 hidden sm:inline">•</span>
              <Link
                to="/terms"
                className="footer-legal-link hover:text-gray-300 transition-colors"
                onClick={() => handleLinkClick('Terms of Service', '/terms')}
              >
                Terms of Service
              </Link>
              <span className="footer-legal-separator text-gray-900 hidden sm:inline">•</span>
              <span className="footer-legal-link">
                {isCompanyPage ? 'Security-First' : 'HIPAA-Ready'}
              </span>
            </div>

            {/* Copyright */}
            <p className="footer-copyright">
              {BRANDING.copyright.text}
            </p>

            {/* Powered By */}
            <p className="footer-powered">
              Powered by{' '}
              <a
                href="https://aimepic.com"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-powered-link"
                onClick={() => handleLinkClick('Aimepic Corporate', 'https://aimepic.com')}
              >
                aimepic.com
              </a>
            </p>

            {/* Additional Legal/Medical Disclaimers */}
            <div className="footer-disclaimers mt-6 pt-6 border-t border-gray-700 text-xs text-gray-800 max-w-4xl mx-auto">
              <p className="footer-disclaimer-medical mb-2">
                {isCompanyPage
                  ? `${BRANDING.company.name} builds secure software for organizations and individuals. Our platforms support human-led decision making and keep users in control of their data and workflows.`
                  : `${BRANDING.company.name} provides enterprise software for healthcare organizations. The platform supports human-led decision making and requires verified users to initiate workflows according to each organization's policies.`}
              </p>
              <p className="footer-disclaimer-emergency">
                For clinical emergencies, dial your national emergency number. {BRANDING.product.name} applications do not replace medical judgment or emergency services.
              </p>
            </div>

            {/* Accessibility Statement */}
            <div className="footer-accessibility mt-4 text-xs text-gray-800">
              <p className="footer-accessibility-text">
                We are committed to accessibility. If you encounter any barriers using the platform or this website, contact{' '}
                <a href="mailto:contact-us@aimepic.com" className="footer-accessibility-link text-primary-400 hover:text-primary-300">
                  contact-us@aimepic.com
                </a>{' '}
                and our team will respond within two business days.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
